import { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute, PremiumRoute, AdminGate } from './components/RouteGuards';

const lazyWithRetry = (importFn) => {
  return lazy(() =>
    importFn().catch((error) => {
      const message = error?.message || '';
      const chunkFailed = message.includes('Loading chunk') || message.includes('ChunkLoadError');
      if (chunkFailed && typeof window !== 'undefined') {
        const key = 'duckers:chunk-reload';
        if (!window.sessionStorage.getItem(key)) {
          window.sessionStorage.setItem(key, 'true');
          window.location.reload();
        }
      }
      throw error;
    })
  );
};

const Home = lazyWithRetry(() => import('./pages/Home'));
const Login = lazyWithRetry(() => import('./pages/Login'));
const Signup = lazyWithRetry(() => import('./pages/Signup'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const Feed = lazyWithRetry(() => import('./pages/Feed'));
const FeedPost = lazyWithRetry(() => import('./pages/FeedPost'));
const CreateDuck = lazyWithRetry(() => import('./pages/CreateDuck'));
const ViewDucks = lazyWithRetry(() => import('./pages/ViewDucks'));
const DuckProfile = lazyWithRetry(() => import('./pages/DuckProfile'));
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const PrivateDucks = lazyWithRetry(() => import('./pages/PrivateDucks'));
const GotDucked = lazyWithRetry(() => import('./pages/GotDucked'));
const EditDuck = lazyWithRetry(() => import('./pages/EditDuck'));
const AdminFlags = lazyWithRetry(() => import('./pages/AdminFlags'));
const AdminConfig = lazyWithRetry(() => import('./pages/AdminConfig'));
const AdminMetrics = lazyWithRetry(() => import('./pages/AdminMetrics'));
const HealthCheck = lazyWithRetry(() => import('./pages/HealthCheck'));
const FollowingManager = lazyWithRetry(() => import('./pages/FollowingManager'));
const AdminSocial = lazyWithRetry(() => import('./pages/AdminSocial'));
const AdminPremium = lazyWithRetry(() => import('./pages/AdminPremium'));
const MyGarage = lazyWithRetry(() => import('./pages/MyGarage'));
const JeepProfile = lazyWithRetry(() => import('./pages/JeepProfile'));
const SwapMeet = lazyWithRetry(() => import('./pages/SwapMeet'));
const PrivacyPolicy = lazyWithRetry(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazyWithRetry(() => import('./pages/TermsOfService'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));
const ShopSuccess = lazyWithRetry(() => import('./pages/ShopSuccess'));
const TrophyRoom = lazyWithRetry(() => import('./pages/TrophyRoom'));
const Leaderboards = lazyWithRetry(() => import('./pages/Leaderboards'));
const AdminBadges = lazyWithRetry(() => import('./pages/AdminBadges'));
const UserProfile = lazyWithRetry(() => import('./pages/UserProfile'));
const Friends = lazyWithRetry(() => import('./pages/Friends'));
const FriendRequests = lazyWithRetry(() => import('./pages/FriendRequests'));
const MessagesInbox = lazyWithRetry(() => import('./pages/MessagesInbox'));
const Conversation = lazyWithRetry(() => import('./pages/Conversation'));
const BlockedList = lazyWithRetry(() => import('./pages/BlockedList'));
const Notifications = lazyWithRetry(() => import('./pages/Notifications'));
const AdminDashboard = lazyWithRetry(() => import('./pages/AdminDashboard'));
const TokenShop = lazyWithRetry(() => import('./pages/TokenShop'));
const FAQ = lazyWithRetry(() => import('./pages/FAQ'));
const TagOrders = lazyWithRetry(() => import('./pages/TagOrders'));
const AdminTagOrders = lazyWithRetry(() => import('./pages/AdminTagOrders'));
const CompleteProfile = lazyWithRetry(() => import('./pages/CompleteProfile'));

export function AppRoutes({ user, isPremium, premiumLoading }) {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={user ? <Navigate to="/feed" replace /> : <Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/duck/:duckId" element={<DuckProfile />} />
      <Route path="/got-ducked" element={<GotDucked />} />
      <Route path="/view-ducks" element={<ViewDucks />} />
      <Route path="/pond" element={<Navigate to="/view-ducks" replace />} />
      <Route path="/shop" element={<TokenShop />} />
      <Route path="/shop/cancel" element={<Navigate to="/shop" replace />} />
      <Route path="/jeep/:jeepId" element={<JeepProfile />} />
      <Route path="/leaderboards" element={<Leaderboards />} />
      <Route path="/trophy-room" element={<TrophyRoom />} />
      <Route path="/trophy-room/:uid" element={<TrophyRoom />} />
      <Route path="/user/:uid" element={<UserProfile />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/faq" element={<FAQ />} />

      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute user={user}><Dashboard user={user} isPremium={isPremium} /></ProtectedRoute>} />
      <Route path="/friends" element={<ProtectedRoute user={user}><Friends /></ProtectedRoute>} />
      <Route path="/friends/requests" element={<ProtectedRoute user={user}><FriendRequests /></ProtectedRoute>} />
      <Route path="/messages/:conversationId" element={<ProtectedRoute user={user}><Conversation /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute user={user}><MessagesInbox /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute user={user}><Notifications /></ProtectedRoute>} />
      <Route path="/blocked" element={<ProtectedRoute user={user}><BlockedList /></ProtectedRoute>} />
      <Route path="/following" element={<ProtectedRoute user={user}><FollowingManager /></ProtectedRoute>} />
      <Route path="/create-duck" element={<ProtectedRoute user={user}><CreateDuck user={user} /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute user={user}><Settings user={user} /></ProtectedRoute>} />
      <Route path="/complete-profile" element={<ProtectedRoute user={user}><CompleteProfile /></ProtectedRoute>} />
      <Route path="/private-ducks" element={<ProtectedRoute user={user}><PrivateDucks /></ProtectedRoute>} />
      <Route path="/duck/:duckId/edit" element={<ProtectedRoute user={user}><EditDuck /></ProtectedRoute>} />
      <Route path="/shop/success" element={<ProtectedRoute user={user}><ShopSuccess /></ProtectedRoute>} />
      <Route path="/tag-orders" element={<ProtectedRoute user={user}><TagOrders /></ProtectedRoute>} />

      {/* Feed – open to all logged-in users; premium gates interactions */}
      <Route path="/feed" element={
        <ProtectedRoute user={user}>
          <Feed user={user} isPremium={isPremium} premiumLoading={premiumLoading} />
        </ProtectedRoute>
      } />
      <Route path="/feed/post/:source/:id" element={
        <ProtectedRoute user={user}>
          <FeedPost user={user} isPremium={isPremium} />
        </ProtectedRoute>
      } />
      <Route path="/garage" element={
        <PremiumRoute user={user} isPremium={isPremium} checking={premiumLoading} feature="Garage" description="Show off your Jeep collection and customize your garage.">
          <MyGarage />
        </PremiumRoute>
      } />
      <Route path="/swap-meet" element={
        <PremiumRoute user={user} isPremium={isPremium} checking={premiumLoading} feature="Swap Meet" description="Trade and sell duck-related items with the community.">
          <SwapMeet user={user} />
        </PremiumRoute>
      } />

      {/* Admin routes */}
      <Route path="/admin" element={<ProtectedRoute user={user}><AdminGate user={user}><AdminDashboard /></AdminGate></ProtectedRoute>} />
      <Route path="/admin/flags" element={<ProtectedRoute user={user}><AdminGate user={user}><AdminFlags /></AdminGate></ProtectedRoute>} />
      <Route path="/admin/badges" element={<ProtectedRoute user={user}><AdminGate user={user}><AdminBadges /></AdminGate></ProtectedRoute>} />
      <Route path="/admin/config" element={<ProtectedRoute user={user}><AdminGate user={user}><AdminConfig /></AdminGate></ProtectedRoute>} />
      <Route path="/admin/metrics" element={<ProtectedRoute user={user}><AdminGate user={user}><AdminMetrics /></AdminGate></ProtectedRoute>} />
      <Route path="/admin/health" element={<ProtectedRoute user={user}><AdminGate user={user}><HealthCheck /></AdminGate></ProtectedRoute>} />
      <Route path="/admin/social" element={<ProtectedRoute user={user}><AdminGate user={user}><AdminSocial /></AdminGate></ProtectedRoute>} />
      <Route path="/admin/premium" element={<ProtectedRoute user={user}><AdminGate user={user}><AdminPremium /></AdminGate></ProtectedRoute>} />
      <Route path="/admin/tag-orders" element={<ProtectedRoute user={user}><AdminGate user={user}><AdminTagOrders /></AdminGate></ProtectedRoute>} />

      {/* Catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
